
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ApiServiceStats } from '@/types/dashboard';

interface ApiStatsChartProps {
  apiStats: ApiServiceStats[];
  loading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export const ApiStatsChart: React.FC<ApiStatsChartProps> = ({ apiStats, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>API-kall per tjeneste</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Responstider</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatServiceName = (name: string) => {
    switch (name) {
      case 'enhetsregisteret': return 'Enhetsregisteret';
      case 'arbeidsplassen': return 'Arbeidsplassen';
      case 'utdanning': return 'Utdanning.no';
      default: return name;
    }
  };

  const pieData = apiStats.map((stat, index) => ({
    name: formatServiceName(stat.service_name),
    value: stat.call_count,
    color: COLORS[index % COLORS.length],
  }));

  const responseTimeData = apiStats.map(stat => ({
    name: formatServiceName(stat.service_name),
    responseTime: parseFloat(stat.avg_response_time.toString()),
    errorRate: parseFloat(stat.error_rate.toString()),
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>API-kall per tjeneste</CardTitle>
          <CardDescription>Siste 7 dager</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Gjennomsnittlig responstid</CardTitle>
          <CardDescription>Millisekunder per tjeneste</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={responseTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="responseTime" fill="#8884d8" name="Responstid (ms)" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
